import { useState } from 'react'
import { ArrowUturnLeftIcon, EyeIcon } from '@/components/Icons'
import { useUiStrings } from '@/hooks/useUiStrings'
import { useStore } from '@/store/useStore'
import { formatDateTime } from '@/utils/formatDateTime'
import {
  canRestoreProjectContextVersion,
  type ProjectContextVersion,
} from '../projectContextUi'
import ProjectContextVersionModal from './ProjectContextVersionModal'

type Props = {
  versions: ProjectContextVersion[]
  /** Fetches segments / snapshot when the detail modal opens. */
  onLoadVersionDetail?: (version: ProjectContextVersion) => Promise<ProjectContextVersion>
  /** Puts an earlier version back in use. */
  onRestoreVersion?: (version: ProjectContextVersion) => void
  /** Opens the meeting a version came from. */
  onOpenMeeting?: (version: ProjectContextVersion) => void
  busy?: boolean
}

const viewButtonClass =
  'inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-indigo-200 bg-indigo-50/60 px-2.5 py-1 text-[11px] font-semibold text-indigo-600 transition-colors hover:bg-indigo-50 disabled:opacity-50 dark:border-indigo-500/40 dark:bg-indigo-500/10 dark:text-[var(--accent)]'

const restoreButtonClass =
  'inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-[var(--border-color)] px-2.5 py-1 text-[11px] font-semibold text-[var(--text-secondary)] transition-colors hover:bg-[var(--bg-tertiary)] hover:text-[var(--text-primary)] disabled:opacity-50'

/**
 * "Context versions" card: one row per AI version.
 *
 * The active row offers View; every earlier AI version offers Restore. Event
 * history keeps the diff behind the same modal, so restoring always shows what
 * is about to be put back first.
 */
export default function ProjectContextVersionsCard({
  versions,
  onLoadVersionDetail,
  onRestoreVersion,
  onOpenMeeting,
  busy = false,
}: Props) {
  const { t } = useUiStrings()
  const uiLocale = useStore((s) => s.uiLocale)
  // The row that has its detail open. Restoring goes through it, so the reader
  // sees what they are about to put back before it happens.
  const [detail, setDetail] = useState<ProjectContextVersion | null>(null)
  const [detailLoading, setDetailLoading] = useState(false)

  async function openDetail(version: ProjectContextVersion) {
    setDetail(version)
    if (!onLoadVersionDetail) return
    setDetailLoading(true)
    try {
      const full = await onLoadVersionDetail(version)
      setDetail(full)
    } catch {
      // Keep the list shell; segments stay empty until a retry.
    } finally {
      setDetailLoading(false)
    }
  }

  return (
    <section
      data-testid="project-versions-card"
      className="flex flex-col gap-4 rounded-2xl border border-[var(--border-color)]/80 bg-[var(--bg-secondary)] p-[25px] shadow-[var(--shadow-sm)]"
    >
      <div className="flex flex-col gap-0.5">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-semibold tracking-[-0.4px] text-[var(--text-primary)]">
            {t.projectDetailVersionsTitle}
          </h2>
          <span className="rounded-full bg-[var(--bg-tertiary)] px-2 py-0.5 text-xs font-semibold tabular-nums text-[var(--text-secondary)]">
            {versions.length}
          </span>
        </div>
        <p className="text-xs leading-4 text-[var(--text-secondary)]">
          {t.projectDetailVersionsHint}
        </p>
      </div>

      {versions.length === 0 ? (
        <p className="py-6 text-center text-sm text-[var(--text-tertiary)]">
          {t.projectDetailVersionsEmpty}
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table
            data-testid="project-versions-table"
            className="w-full border-collapse text-left text-xs"
          >
            <thead>
              <tr className="border-b border-slate-100 font-semibold uppercase tracking-wide text-slate-400 dark:border-[var(--border-color)] dark:text-[var(--text-tertiary)]">
                <th className="w-16 pb-3">{t.projectDetailVersionsColVersion}</th>
                <th className="w-36 pb-3">{t.projectDetailVersionsColSource}</th>
                <th className="w-36 pb-3">{t.projectDetailVersionsColDate}</th>
                <th className="pb-3">{t.projectDetailVersionsColChanges}</th>
                <th className="w-28 pb-3 pr-2 text-right">
                  {t.projectDetailVersionsColStatus}
                </th>
                <th className="w-32 pb-3 text-right">{t.projectDetailVersionsColActions}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100 text-slate-600 dark:divide-[var(--border-color)] dark:text-[var(--text-secondary)]">
              {versions.map((version) => {
                const canRestore = canRestoreProjectContextVersion(version)
                return (
                  <tr key={version.id} className="transition-colors hover:bg-[var(--bg-primary)]/50">
                    <td className="py-[18px] font-bold text-[var(--text-primary)]">{version.label}</td>
                    <td className="py-[18px]">
                      {version.sourceIsMeeting ? (
                        <button
                          type="button"
                          disabled={busy}
                          onClick={() => onOpenMeeting?.(version)}
                          className="text-indigo-600 hover:underline disabled:opacity-50 dark:text-[var(--accent)]"
                        >
                          {version.sourceLabel}
                        </button>
                      ) : (
                        <span className="text-[var(--text-primary)]">{version.sourceLabel}</span>
                      )}
                    </td>
                    <td className="py-[18px] text-slate-500 dark:text-[var(--text-secondary)]">
                      {formatDateTime(version.createdAt, uiLocale)}
                    </td>
                    <td className="py-[18px] text-slate-600 dark:text-[var(--text-secondary)]">
                      {version.changes}
                    </td>
                    <td className="py-[18px] pr-1 text-right">
                      {version.active ? (
                        <span className="inline-flex items-center rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium leading-4 text-emerald-600 dark:bg-emerald-500/15 dark:text-emerald-400">
                          {t.projectDetailVersionsActive}
                        </span>
                      ) : null}
                    </td>
                    <td className="py-[18px] text-right">
                      {version.active ? (
                        <button
                          type="button"
                          disabled={busy || detailLoading}
                          aria-label={`${t.projectDetailVersionsView}: ${version.label}`}
                          onClick={() => void openDetail(version)}
                          className={viewButtonClass}
                        >
                          <EyeIcon className="h-3.5 w-3.5" />
                          {t.projectDetailVersionsView}
                        </button>
                      ) : canRestore ? (
                        <button
                          type="button"
                          disabled={busy || detailLoading}
                          aria-label={`${t.projectDetailVersionsRestore}: ${version.label}`}
                          onClick={() => void openDetail(version)}
                          className={restoreButtonClass}
                        >
                          <ArrowUturnLeftIcon className="h-3.5 w-3.5" />
                          {t.projectDetailVersionsRestore}
                        </button>
                      ) : (
                        <button
                          type="button"
                          disabled={busy || detailLoading}
                          aria-label={`${t.projectDetailVersionsView}: ${version.label}`}
                          onClick={() => void openDetail(version)}
                          className={viewButtonClass}
                        >
                          <EyeIcon className="h-3.5 w-3.5" />
                          {t.projectDetailVersionsView}
                        </button>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}

      <ProjectContextVersionModal
        version={detail}
        busy={busy || detailLoading}
        onClose={() => setDetail(null)}
        onRestore={(version) => {
          onRestoreVersion?.(version)
          setDetail(null)
        }}
      />
    </section>
  )
}
